<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  dynamicTime,
  relativeTime,
  dateFormat,
} from 'shared/helpers/timeHelper';

export default {
  name: 'TimeAgo',
  props: {
    timestampDisplayMode: {
      type: String,
      default: 'both',
      validator: value =>
        ['both', 'last_activity_only', 'last_customer_message'].includes(value),
    },
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    lastCustomerMessageTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
  },
  data() {
    return {
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp),
      lastCustomerMessageAtTimeAgo: dynamicTime(
        this.lastCustomerMessageTimestamp
      ),
      timer: null,
    };
  },
  computed: {
    createdAtTime() {
      return relativeTime(this.createdAtTimestamp);
    },
    lastActivityTime() {
      return relativeTime(this.lastActivityTimestamp);
    },
    lastCustomerMessageTime() {
      return relativeTime(this.lastCustomerMessageTimestamp);
    },
    createdAtFullDate() {
      return dateFormat(this.createdAtTimestamp, 'd.M.yyyy H:mm:ss');
    },
    lastActivityFullDate() {
      return dateFormat(this.lastActivityTimestamp, 'd.M.yyyy H:mm:ss');
    },
    lastCustomerMessageFullDate() {
      return dateFormat(this.lastCustomerMessageTimestamp, 'd.M.yyyy H:mm:ss');
    },
    createdAt() {
      return `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED')} ${this.createdAtFullDate}`;
    },
    lastActivity() {
      return `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY')} ${this.lastActivityFullDate}`;
    },
    lastCustomerMessage() {
      return `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_CUSTOMER_MESSAGE')} ${this.lastCustomerMessageFullDate}`;
    },
    tooltipText() {
      const lines = [this.createdAt, this.lastActivity];
      if (this.lastCustomerMessageTimestamp) {
        lines.push(this.lastCustomerMessage);
      }
      return lines.join('<br>');
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
    },
    lastCustomerMessageTimestamp() {
      this.lastCustomerMessageAtTimeAgo = dynamicTime(
        this.lastCustomerMessageTimestamp
      );
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
        this.lastCustomerMessageAtTimeAgo = dynamicTime(
          this.lastCustomerMessageTimestamp
        );
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <div
    v-tooltip.top="{
      content: tooltipText,
      delay: { show: 1000, hide: 0 },
      html: true,
    }"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-slate-11"
  >
    <span v-if="timestampDisplayMode === 'both'">{{
      `${createdAtTime} • ${lastActivityTime}`
    }}</span>
    <span v-else-if="timestampDisplayMode === 'last_customer_message'">{{
      lastCustomerMessageTimestamp ? lastCustomerMessageTime : lastActivityTime
    }}</span>
    <span v-else>{{ lastActivityTime }}</span>
  </div>
</template>
